/* eslint-disable react-refresh/only-export-components -- TanStack routes export Route beside page components. */
import { lazy, Suspense, useState } from "react";
import {
  Link,
  createFileRoute,
  useParams,
  useSearch,
} from "@tanstack/react-router";
import { useMutation, useQuery } from "convex/react";
import { ArrowLeft, Box, History, Images, Plus, RotateCcw, Ruler, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";

import { api } from "../../convex/_generated/api";
import type { Id } from "../../convex/_generated/dataModel";
import type { FunctionReturnType } from "convex/server";
import { Authenticated, AuthLoading, Unauthenticated } from "@/components/auth-gate";
import { Button } from "@/components/ui/button";
import { RoomComposer } from "@/components/visit/room-composer";
import { BESIDE_PANEL, type PanelTab } from "@/components/visit/room-panel";
import { RemoveRoomDialog, RoomChip, RoomStage } from "@/components/visit/room-stage";
import { STAGE_ROWS } from "@/components/visit/stage-layout";
import { STAGE_FOCUS, StageMessage } from "@/components/visit/stage-message";
import { VersionsDialog } from "@/components/visit/versions-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Toaster } from "@/components/ui/sonner";
import { Splash } from "@/components/shell/splash";
import { RedirectToLogin } from "@/components/shell/redirect-to-login";
import { boardName } from "@/i18n/catalog";
import { cn } from "@/lib/utils";
import { said } from "@/lib/errors";

const VIEW_KEYS = [
  "far",
  "farRight",
  "right",
  "nearRight",
  "near",
  "nearLeft",
  "left",
  "farLeft",
] as const;
/** Views are stored in the order they're made, so label them by bearing. */
function viewKey(azimuth: number | undefined) {
  return azimuth === undefined ? undefined : VIEW_KEYS[Math.round(azimuth / 45) % 8];
}

const SplatViewer = lazy(() =>
  import("@/components/visit/splat-viewer").then((m) => ({ default: m.SplatViewer })),
);

export const Route = createFileRoute("/boards/$boardId_/visit")({
  // ?in=app: opened inside the app, which has a close button, the versions and the build of its own; the
  // page then shows the room alone. `top` and `bottom` are what the phone keeps for itself above and
  // under the page, in px.
  validateSearch: (search): { in?: "app"; top?: number; bottom?: number } => ({
    in: search.in === "app" ? "app" : undefined,
    top: typeof search.top === "number" && Number.isFinite(search.top) ? Math.max(0, search.top) : undefined,
    bottom: typeof search.bottom === "number" && Number.isFinite(search.bottom) ? Math.max(0, search.bottom) : undefined,
  }),
  component: VisitPage,
});

function VisitPage() {
  const { t: tc } = useTranslation();
  return (
    <>
      <AuthLoading>
        <Splash subtitle={tc("status.loading")} />
      </AuthLoading>
      <Unauthenticated>
        <RedirectToLogin />
      </Unauthenticated>
      <Authenticated>
        <SignedInVisit />
      </Authenticated>
      <Toaster />
    </>
  );
}

type Scene = FunctionReturnType<typeof api.scenes.listForBoard>[number];

function SignedInVisit() {
  const { t } = useTranslation("visit");
  const { t: tc } = useTranslation();
  const { boardId } = useParams({ from: "/boards/$boardId_/visit" });
  const { in: opener, top, bottom } = useSearch({ from: "/boards/$boardId_/visit" });
  const inApp = opener === "app";
  const typedBoardId = boardId as Id<"boards">;
  const board = useQuery(api.boards.get, { boardId: typedBoardId });
  // What the viewer may do: a client of the team may be let only look, or not even see the 3D.
  const me = useQuery(api.workspaces.getMyWorkspace);
  const canEdit = me?.can.editRoom ?? false;
  const isTeam = me?.role === "team";
  const [versionsOpen, setVersionsOpen] = useState(false);
  const scenes = useQuery(api.scenes.listForBoard, { boardId: typedBoardId });
  // Blender rooms only exist where the deployment has the keys to build them;
  // without, nothing about them is asked for or shown.
  const roomsEnabled = useQuery(api.blenderRooms.availability)?.enabled;
  const rooms = useQuery(api.blenderRooms.listForBoard, roomsEnabled ? { boardId: typedBoardId } : "skip");

  const [selectedId, setSelectedId] = useState<Id<"scenes"> | Id<"blenderRooms"> | null>(null);
  // The room in the strip that was picked, else the newest, which is also the one the phone app shows.
  const pickedRoom = rooms?.find((r) => r.roomId === selectedId);
  const roomId = (pickedRoom ?? rooms?.[0])?.roomId;
  const room = useQuery(api.blenderRooms.getForBoard, roomId ? { boardId: typedBoardId, roomId } : "skip");

  const [removing, setRemoving] = useState<Scene | null>(null);
  const [roomComposerOpen, setRoomComposerOpen] = useState(false);
  // The room the form builds again; null for a new one beside the others.
  const [composingFor, setComposingFor] = useState<Id<"blenderRooms"> | null>(null);
  // Which room, not just whether: one built after this one was removed elsewhere is not the room asked about.
  const [removingRoom, setRemovingRoom] = useState<Id<"blenderRooms"> | null>(null);
  const [roomComposerKey, setRoomComposerKey] = useState(0);
  const openRoomComposer = (target: Id<"blenderRooms"> | null) => {
    setComposingFor(target);
    setRoomComposerKey((k) => k + 1);
    setRoomComposerOpen(true);
  };
  // Where the room's share button goes in the bar: the stage puts it there, it knows what is on screen.
  const [shareSlot, setShareSlot] = useState<HTMLElement | null>(null);
  // The part of the room's panel that is open: kept here, where the strip makes way for it.
  const [roomPanel, setRoomPanel] = useState<PanelTab | null>(null);

  // The visits Marble built before it was retired: all of them ready to walk through.
  const pickedScene = scenes?.find((s) => s._id === selectedId);
  // Nothing picked: the room comes first, unless it isn't ready and an old visit is.
  const shownRoom =
    room && !pickedScene && (pickedRoom || room.status === "ready" || !scenes?.length) ? room : null;
  const selected = shownRoom ? null : (pickedScene ?? scenes?.[0] ?? null);
  // Only a room that stands has a panel: its pieces, and the thread where changes to it are asked for.
  const panel = shownRoom?.spec ? roomPanel : null;
  // Going from one room to another: the strip stays, while the stage waits for the room picked.
  const roomLoading = roomId !== undefined && room === undefined && !pickedScene;
  const rebuilt = room && room.roomId === composingFor ? room : null;

  if (
    board === undefined ||
    scenes === undefined ||
    roomsEnabled === undefined ||
    (roomsEnabled && rooms === undefined)
  ) {
    return <Splash subtitle={tc("status.loading")} />;
  }
  if (board === null) {
    return <Splash subtitle={t("notFound")} />;
  }

  return (
    <main
      className="relative h-svh w-full overflow-hidden bg-[#0e0d0c] text-white"
      // What the device keeps under the page: the app says, a browser's safe area says for itself. And where
      // a phone's top begins: under the app's close button (8px, 44px of it, 8px), or under the page's own header.
      style={
        {
          "--inset-bottom": inApp ? `${bottom ?? 0}px` : "env(safe-area-inset-bottom, 0px)",
          "--stage-head": inApp ? `${(top ?? 0) + 60}px` : "4rem",
        } as React.CSSProperties
      }
    >
      {shownRoom ? (
        <RoomStage
          key={shownRoom.roomId}
          boardId={typedBoardId}
          room={shownRoom}
          panel={panel}
          onPanel={setRoomPanel}
          onBuild={() => openRoomComposer(shownRoom.roomId)}
          onRemove={() => setRemovingRoom(shownRoom.roomId)}
          strip={!inApp}
          canEdit={canEdit}
          share={shareSlot ? { slot: shareSlot, board: boardName(board.name) } : null}
        />
      ) : roomLoading ? (
        <StageMessage>{t("stage.loadingViewer")}</StageMessage>
      ) : selected ? (
        <SceneStage key={selected._id} scene={selected} />
      ) : (
        <EmptyStage onBuildRoom={roomsEnabled && canEdit ? () => openRoomComposer(null) : undefined} />
      )}

      {/* Top bar. Not in the app, which closes the page, builds the room again and removes it from its own screens. */}
      {inApp ? null : (
      <div className="pointer-events-none absolute inset-x-0 top-0 flex h-14 items-center justify-between gap-4 bg-gradient-to-b from-black/60 to-transparent px-5">
        <Link
          to="/boards/$boardId"
          params={{ boardId }}
          className="pointer-events-auto group inline-flex items-center gap-1.5 text-xs uppercase tracking-wide text-white/70 transition-colors hover:text-white"
        >
          <ArrowLeft className="size-3.5 transition-transform group-hover:-translate-x-0.5 rtl:-scale-x-100 rtl:group-hover:translate-x-0.5" />
          {boardName(board.name)}
        </Link>
        {shownRoom ? (
          <div className="pointer-events-auto flex items-center gap-5">
            {/* Every saved version, to name one or go back to it. */}
            {shownRoom.version !== null ? (
              <button
                type="button"
                onClick={() => setVersionsOpen(true)}
                title={t("versions.title")}
                className={cn(
                  "relative inline-flex items-center gap-1 text-2xs uppercase tracking-[0.18em] text-white/50 after:absolute after:-inset-3 hover:text-white",
                  STAGE_FOCUS,
                )}
              >
                <History className="size-3" />{" "}
                <span className="max-sm:sr-only">{shownRoom.label ?? t("versions.title")}</span>
              </button>
            ) : null}
            {/* A room that stands can be built again, with other pieces or sizes. */}
            {shownRoom.status === "ready" && canEdit ? (
              <button
                type="button"
                onClick={() => openRoomComposer(shownRoom.roomId)}
                title={t("room.rebuild")}
                className={cn(
                  "relative inline-flex items-center gap-1 text-2xs uppercase tracking-[0.18em] text-white/50 after:absolute after:-inset-3 hover:text-white",
                  STAGE_FOCUS,
                )}
              >
                {/* The icon alone on a phone, where the bar also holds the board's name: small to look at, a full fingertip to hit. */}
                <RotateCcw className="size-3" /> <span className="max-sm:sr-only">{t("room.rebuild")}</span>
              </button>
            ) : null}
            {isTeam ? (
              <button
                type="button"
                onClick={() => setRemovingRoom(shownRoom.roomId)}
                className={cn(
                  "inline-flex items-center gap-1 text-2xs uppercase tracking-[0.18em] text-white/50 hover:text-white",
                  STAGE_FOCUS,
                )}
              >
                <Trash2 className="size-3" /> {tc("actions.remove")}
              </button>
            ) : null}
            {/* The share button, in the corner, once the room stands. */}
            <span ref={setShareSlot} className="contents" />
          </div>
        ) : selected && isTeam ? (
          <div className="pointer-events-auto flex items-center gap-5">
            <button
              type="button"
              onClick={() => setRemoving(selected)}
              className="inline-flex items-center gap-1 text-2xs uppercase tracking-[0.18em] text-white/50 hover:text-white"
            >
              <Trash2 className="size-3" /> {tc("actions.remove")}
            </button>
          </div>
        ) : null}
      </div>
      )}

      {/* Scene strip. The app has the versions on the room's own screen. */}
      {!inApp && (scenes.length > 0 || (rooms?.length ?? 0) > 0) ? (
        // Beside the room's panel on a wide screen; under it on a phone, where the panel is a sheet, and out of reach.
        <div
          className={cn(
            "absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent px-5 pb-5 pt-10",
            // On a phone, above the row of the room's controls along the bottom.
            shownRoom?.spec && [STAGE_ROWS, "max-sm:bottom-[var(--row-top)] max-sm:pb-0"],
            panel && [BESIDE_PANEL, "max-md:invisible"],
          )}
        >
          {/* Where rooms are built: space around the chips for their focus ring, which the scrolling would cut off. */}
          <div className={cn("flex items-end gap-2 overflow-x-auto", roomsEnabled && "-m-1.5 p-1.5")}>
            {/* In the order they were built: a new one takes the place of the button after them. */}
            {rooms?.toReversed().map((r, index) => (
              <RoomChip
                key={r.roomId}
                room={r}
                n={rooms.length > 1 ? index + 1 : null}
                active={r.roomId === shownRoom?.roomId}
                onSelect={() => setSelectedId(r.roomId)}
              />
            ))}
            {scenes.map((s) => (
              <SceneChip
                key={s._id}
                scene={s}
                active={s._id === selected?._id}
                onSelect={() => setSelectedId(s._id)}
              />
            ))}
            {roomsEnabled && canEdit ? (
              <button
                type="button"
                onClick={() => openRoomComposer(null)}
                className={cn(
                  "grid h-16 w-24 shrink-0 place-items-center rounded-md border border-dashed border-white/25 text-white/60 transition-colors hover:border-white/50 hover:text-white",
                  STAGE_FOCUS,
                )}
                aria-label={t(rooms?.length ? "room.another" : "room.new")}
                title={t(rooms?.length ? "room.another" : "room.new")}
              >
                {rooms?.length ? <Plus className="size-4" /> : <Ruler className="size-4" />}
              </button>
            ) : null}
          </div>
        </div>
      ) : null}

      <RemoveSceneDialog
        scene={removing}
        onOpenChange={(open) => !open && setRemoving(null)}
        onRemoved={(id) => {
          if (selectedId === id) setSelectedId(null);
        }}
      />

      {shownRoom ? (
        <VersionsDialog
          key={shownRoom.roomId}
          boardId={typedBoardId}
          roomId={shownRoom.roomId}
          open={versionsOpen}
          onOpenChange={setVersionsOpen}
          canEdit={canEdit}
        />
      ) : null}

      {roomsEnabled ? (
        <>
          <RemoveRoomDialog
            boardId={typedBoardId}
            room={room && room.roomId === removingRoom ? room : null}
            several={(rooms?.length ?? 0) > 1}
            onOpenChange={(open) => !open && setRemovingRoom(null)}
            onRemoved={() => {
              if (selectedId === removingRoom) setSelectedId(null);
              // The next room built here starts with its panel closed.
              setRoomPanel(null);
            }}
          />
          <RoomComposer
            key={roomComposerKey}
            boardId={typedBoardId}
            roomId={composingFor}
            another={composingFor === null && (rooms?.length ?? 0) > 0}
            open={roomComposerOpen}
            onOpenChange={setRoomComposerOpen}
            onStarted={setSelectedId}
            inRoom={rebuilt?.spec?.items.flatMap((item) => (item.itemId ? [item.itemId] : []))}
            // A change that failed left the room as it was: only a build is tried again as it was.
            retryOf={rebuilt?.run?.status === "failed" && rebuilt.run.kind === "build" ? rebuilt.run.itemIds : undefined}
          />
        </>
      ) : null}
    </main>
  );
}

/** A visit Marble built: walked through in 3D, with the photos it was built from a tap away. */
function SceneStage({ scene }: { scene: Scene }) {
  const { t } = useTranslation("visit");
  const [showPhotos, setShowPhotos] = useState(false);
  // The photos the room was built from stay viewable once it's in 3D.
  const views = scene.viewUrls.length > 0 ? scene.viewUrls : scene.compositeUrl ? [scene.compositeUrl] : [];

  return (
    <>
      <Suspense fallback={<StageMessage>{t("stage.loadingViewer")}</StageMessage>}>
        <SplatViewer key={scene._id} url={scene.splatUrl} className="absolute inset-0" />
      </Suspense>
      {/* Laid over the viewer so going back to 3D doesn't reload the world. */}
      {showPhotos && views.length > 0 ? (
        <div className="absolute inset-0 grid place-items-center bg-[#0e0d0c] px-4 pb-32 pt-28">
          <ViewGrid scene={scene} views={views} />
        </div>
      ) : null}
      {views.length > 0 ? (
        <div className="absolute inset-x-0 top-14 flex justify-center">
          <div className="flex rounded-full border border-white/15 bg-black/50 p-0.5 text-2xs uppercase tracking-[0.14em] backdrop-blur">
            {([false, true] as const).map((photos) => (
              <button
                key={String(photos)}
                type="button"
                aria-pressed={showPhotos === photos}
                onClick={() => setShowPhotos(photos)}
                className={cn(
                  "inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 transition-colors",
                  showPhotos === photos ? "bg-white text-black" : "text-white/60 hover:text-white",
                )}
              >
                {photos ? <Images className="size-3" /> : <Box className="size-3" />}
                {photos ? t("stage.showPhotos") : t("stage.show3d")}
              </button>
            ))}
          </div>
        </div>
      ) : null}
    </>
  );
}

/** The generated photos of a scene, labelled by the way each one faces. */
function ViewGrid({
  scene,
  views,
  className,
}: {
  scene: Scene;
  views: string[];
  className?: string;
}) {
  const { t } = useTranslation("visit");
  return (
    <div
      className={cn(
        "grid max-h-[65svh] gap-2 overflow-y-auto transition-opacity",
        views.length > 4 ? "grid-cols-2 sm:grid-cols-4" : views.length > 1 ? "grid-cols-2" : "grid-cols-1",
        className,
      )}
    >
      {views.map((url, i) => {
        const key = viewKey(scene.viewAzimuths?.[i]);
        const label = key ? t(`views.${key}`) : undefined;
        return (
          <img
            key={url}
            src={url}
            alt={label ?? t("stage.photoAlt", { n: i + 1 })}
            title={label}
            className="w-full rounded-lg object-contain shadow-2xl"
          />
        );
      })}
    </div>
  );
}

/** Confirms deleting a scene: its photos and, if built, its 3D world. */
function RemoveSceneDialog({
  scene,
  onOpenChange,
  onRemoved,
}: {
  scene: Scene | null;
  onOpenChange: (open: boolean) => void;
  onRemoved: (id: Id<"scenes">) => void;
}) {
  const { t } = useTranslation("visit");
  const { t: tc } = useTranslation();
  const removeScene = useMutation(api.scenes.remove);
  const [busy, setBusy] = useState(false);

  const confirm = async () => {
    if (!scene) return;
    setBusy(true);
    try {
      await removeScene({ sceneId: scene._id });
      onRemoved(scene._id);
      onOpenChange(false);
    } catch (err) {
      toast.error(said(err, t("remove.failed")));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={scene !== null} onOpenChange={(open) => !busy && onOpenChange(open)}>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle>{t("remove.title")}</DialogTitle>
          <DialogDescription>{t("remove.bodyReady")}</DialogDescription>
        </DialogHeader>
        {scene?.compositeUrl ?? scene?.thumbnailUrl ? (
          <img
            src={(scene.thumbnailUrl ?? scene.compositeUrl)!}
            alt=""
            className="aspect-[3/2] w-full rounded-md object-cover"
          />
        ) : null}
        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="outline" size="sm" onClick={() => onOpenChange(false)} disabled={busy}>
            {t("remove.keep")}
          </Button>
          <Button variant="destructive" size="sm" onClick={confirm} disabled={busy}>
            <Trash2 /> {busy ? t("remove.removing") : tc("actions.remove")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EmptyStage({
  onBuildRoom,
}: {
  /** Starts a Blender room, where the deployment can build one. */
  onBuildRoom?: () => void;
}) {
  const { t } = useTranslation("visit");
  return (
    <StageMessage>
      <p className="font-display text-2xl italic text-white">{t("empty.title")}</p>
      <p className="max-w-sm text-sm text-white/60 text-balance">{t("empty.body")}</p>
      {onBuildRoom ? (
        <Button
          size="sm"
          className={cn("mt-3 bg-white text-black hover:bg-white/90", STAGE_FOCUS)}
          onClick={onBuildRoom}
        >
          <Ruler /> {t("room.emptyCta")}
        </Button>
      ) : null}
    </StageMessage>
  );
}

function SceneChip({
  scene,
  active,
  onSelect,
}: {
  scene: Scene;
  active: boolean;
  onSelect: () => void;
}) {
  const thumbnail = scene.thumbnailUrl ?? scene.compositeUrl;
  return (
    <button
      type="button"
      onClick={onSelect}
      className={cn(
        "relative h-16 w-24 shrink-0 overflow-hidden rounded-md border bg-white/5 transition-[border-color,transform] active:scale-[0.97]",
        active ? "border-white" : "border-white/15 hover:border-white/40",
        STAGE_FOCUS,
      )}
      title={scene.prompt}
    >
      {thumbnail ? <img src={thumbnail} alt="" className="size-full object-cover" /> : null}
    </button>
  );
}
